import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stripeApi, authApi, storefrontAdminApi } from '@/services/api';
import toast from 'react-hot-toast';
import {
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  KeyRound,
  Globe,
  Eye,
  EyeOff,
  Zap,
} from 'lucide-react';

export default function StripeSettingsTab({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    account_id?: string;
    business_name?: string;
    currencies?: string[];
    mode?: string;
    error?: string;
  } | null>(null);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const { data: settings, isLoading } = useQuery({
    queryKey: ['tenantStripeSettings'],
    queryFn: async () => {
      const res = await stripeApi.getTenantSettings();
      return res.data.message;
    },
  });

  const { data: storeSettings } = useQuery({
    queryKey: ['storefrontAdminSettings'],
    queryFn: async () => (await storefrontAdminApi.getSettings()).data.message,
    enabled: embedded,
  });

  const [form, setForm] = useState({
    enabled: false,
    mode: 'Sandbox' as 'Sandbox' | 'Live',
    checkout_type: 'redirect' as 'redirect' | 'embedded',
    currency: 'GBP',
    statement_descriptor: '',
    auto_complete_order: true,
    test_publishable_key: '',
    test_secret_key: '',
    test_webhook_secret: '',
    live_publishable_key: '',
    live_secret_key: '',
    live_webhook_secret: '',
  });

  // Hydrate from server settings only (not profile) so a late profile fetch
  // does not wipe an in-progress enable/disable toggle.
  useEffect(() => {
    if (!settings) return;
    setForm({
      enabled: Number(settings.enabled) === 1 || settings.enabled === true,
      mode: settings.mode || 'Sandbox',
      checkout_type: settings.checkout_type || 'redirect',
      currency: settings.currency || profile?.currency || 'GBP',
      statement_descriptor:
        settings.statement_descriptor || profile?.company_name?.slice(0, 22) || '',
      auto_complete_order: settings.auto_complete_order !== 0,
      test_publishable_key: settings.test_publishable_key || '',
      test_secret_key: '',
      test_webhook_secret: '',
      live_publishable_key: settings.live_publishable_key || '',
      live_secret_key: '',
      live_webhook_secret: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- profile is fallback only
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const { _toggleOnly: _ignored, ...payload } = data as Record<string, unknown> & {
        _toggleOnly?: boolean;
      };
      await stripeApi.saveTenantSettings(payload as any);
      if (embedded && payload.enabled) {
        await storefrontAdminApi.saveSettings({
          ...(storeSettings || {}),
          pay_online_stripe: 1,
          pay_online_teya: 0,
          pay_online_dojo: 0,
        });
      }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tenantStripeSettings'] });
      if (embedded) qc.invalidateQueries({ queryKey: ['storefrontAdminSettings'] });
      const togglingOnly = Boolean((variables as { _toggleOnly?: boolean })?._toggleOnly);
      toast.success(
        togglingOnly
          ? (variables as { enabled?: boolean }).enabled
            ? 'Stripe enabled'
            : 'Stripe disabled'
          : 'Stripe settings saved successfully',
      );
      setTestResult(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to save Stripe settings');
    },
  });

  const toggleEnabled = () => {
    if (saveMutation.isPending) return;
    const prev = form.enabled;
    const next = !prev;
    setForm((f) => ({ ...f, enabled: next }));
    saveMutation.mutate({ ...form, enabled: next, _toggleOnly: true } as any, {
      onError: () => setForm((f) => ({ ...f, enabled: prev })),
    });
  };

  const handleCopyWebhook = () => {
    if (settings?.webhook_url) {
      navigator.clipboard.writeText(settings.webhook_url);
      setCopiedWebhook(true);
      toast.success('Webhook URL copied to clipboard');
      setTimeout(() => setCopiedWebhook(false), 2000);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      const activeSecret = form.mode === 'Sandbox' ? form.test_secret_key : form.live_secret_key;
      const res = await stripeApi.testTenantConnection({
        mode: form.mode,
        secret_key: activeSecret || undefined,
      });
      const result = res.data.message;
      setTestResult(result);
      if (result.success) {
        toast.success(`Connected to Stripe (${result.mode} Mode)!`);
      } else {
        toast.error(result.error || 'Connection failed');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Connection test failed';
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // When embedded under Card payment, keep Stripe enabled while configuring
    saveMutation.mutate({ ...form, enabled: embedded ? true : form.enabled } as any);
  };

  const isLive = form.mode === 'Live';
  const hasConfiguredSecret = isLive ? settings?.has_live_secret_key : settings?.has_test_secret_key;
  const hasConfiguredWebhook = isLive ? settings?.has_live_webhook_secret : settings?.has_test_webhook_secret;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-2" />
        <span className="text-sm font-medium">Loading Stripe configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header Banner — master on/off lives on Card payment tab when embedded */}
      {!embedded && (
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 rounded-2xl p-6 text-white shadow-md relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-indigo-500/20 text-indigo-200 border border-indigo-400/30">
                Payment Gateway
              </span>
              <span
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider border ${
                  form.enabled
                    ? isLive
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                      : 'bg-amber-500/20 text-amber-300 border-amber-400/40'
                    : 'bg-gray-500/20 text-gray-400 border-gray-400/30'
                }`}
              >
                {form.enabled ? (isLive ? 'Live Mode' : 'Sandbox Mode') : 'Disabled'}
              </span>
            </div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-400" />
              Stripe Shop Integration
            </h2>
            <p className="text-xs text-indigo-200/80 mt-1 max-w-xl">
              Enable your store to accept card payments, Apple Pay, and Google Pay from customers during POS checkout and online orders.
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            disabled={saveMutation.isPending}
            onClick={toggleEnabled}
            className="inline-flex items-center gap-3 shrink-0 disabled:opacity-60"
          >
            <span
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                form.enabled ? 'bg-indigo-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  form.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </span>
            <span className="text-xs font-bold text-white">
              {form.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </button>
        </div>
      </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Environment Mode & Experience Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Mode Selector */}
          <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-amber-500" />
                Environment Mode
              </label>
              <span className="text-[10px] text-gray-500">Sandbox for testing</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, mode: 'Sandbox' })}
                className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                  form.mode === 'Sandbox'
                    ? 'border-amber-500 bg-amber-50/50 ring-2 ring-amber-500/20'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-amber-900">🧪 Sandbox</span>
                  <input
                    type="radio"
                    name="mode"
                    checked={form.mode === 'Sandbox'}
                    onChange={() => setForm({ ...form, mode: 'Sandbox' })}
                    className="text-amber-600 focus:ring-amber-500"
                  />
                </div>
                <p className="text-[10px] text-gray-500 leading-tight">
                  Safe test mode using Stripe test cards without real charges.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setForm({ ...form, mode: 'Live' })}
                className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                  form.mode === 'Live'
                    ? 'border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-600/20'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-emerald-900">🚀 Live</span>
                  <input
                    type="radio"
                    name="mode"
                    checked={form.mode === 'Live'}
                    onChange={() => setForm({ ...form, mode: 'Live' })}
                    className="text-emerald-600 focus:ring-emerald-500"
                  />
                </div>
                <p className="text-[10px] text-gray-500 leading-tight">
                  Real payment processing into your live bank account.
                </p>
              </button>
            </div>
          </div>

          {/* Checkout Experience */}
          <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                <Globe className="w-4 h-4 text-blue-600" />
                Checkout Experience
              </label>
              <span className="text-[10px] text-gray-500">Customer checkout type</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, checkout_type: 'redirect' })}
                className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                  form.checkout_type === 'redirect'
                    ? 'border-blue-600 bg-blue-50/50 ring-2 ring-blue-600/20'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-blue-900">Redirect to Stripe</span>
                  <input
                    type="radio"
                    name="checkout_type"
                    checked={form.checkout_type === 'redirect'}
                    onChange={() => setForm({ ...form, checkout_type: 'redirect' })}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <p className="text-[10px] text-gray-500 leading-tight">
                  Stripe Hosted Checkout with Apple Pay, Google Pay, & 3D Secure.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setForm({ ...form, checkout_type: 'embedded' })}
                className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                  form.checkout_type === 'embedded'
                    ? 'border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-600/20'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-indigo-900">Embedded Card</span>
                  <input
                    type="radio"
                    name="checkout_type"
                    checked={form.checkout_type === 'embedded'}
                    onChange={() => setForm({ ...form, checkout_type: 'embedded' })}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <p className="text-[10px] text-gray-500 leading-tight">
                  Customer enters card details directly on our website modal.
                </p>
              </button>
            </div>
          </div>
        </div>

        {/* Credentials Form */}
        <div className="card p-6 bg-white border border-gray-200 shadow-sm space-y-5">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary-600" />
              <h3 className="text-sm font-bold text-gray-900">
                {isLive ? 'Live API Credentials' : 'Sandbox (Test) API Credentials'}
              </h3>
            </div>
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors"
            >
              Get Stripe API Keys <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Publishable Key */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-gray-700">
                {isLive ? 'Live Publishable Key' : 'Test Publishable Key'}
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                required={form.enabled}
                placeholder={isLive ? 'pk_live_...' : 'pk_test_...'}
                value={isLive ? form.live_publishable_key : form.test_publishable_key}
                onChange={(e) =>
                  isLive
                    ? setForm({ ...form, live_publishable_key: e.target.value })
                    : setForm({ ...form, test_publishable_key: e.target.value })
                }
                className="w-full text-xs font-mono p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50"
              />
            </div>

            {/* Secret Key */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-700">
                  {isLive ? 'Live Secret Key' : 'Test Secret Key'}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                {hasConfiguredSecret && (
                  <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Configured
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  type={showSecretKey ? 'text' : 'password'}
                  placeholder={
                    hasConfiguredSecret
                      ? '•••••••••••••••••••••••••••••••• (leave blank to keep unchanged)'
                      : isLive
                      ? 'sk_live_...'
                      : 'sk_test_...'
                  }
                  value={isLive ? form.live_secret_key : form.test_secret_key}
                  onChange={(e) =>
                    isLive
                      ? setForm({ ...form, live_secret_key: e.target.value })
                      : setForm({ ...form, test_secret_key: e.target.value })
                  }
                  className="w-full text-xs font-mono p-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Webhook Secret */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-700">
                  {isLive ? 'Live Webhook Secret' : 'Test Webhook Secret'}
                </label>
                {hasConfiguredWebhook && (
                  <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Configured
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  placeholder={
                    hasConfiguredWebhook
                      ? '•••••••••••••••••••••••••••••••• (leave blank to keep unchanged)'
                      : 'whsec_...'
                  }
                  value={isLive ? form.live_webhook_secret : form.test_webhook_secret}
                  onChange={(e) =>
                    isLive
                      ? setForm({ ...form, live_webhook_secret: e.target.value })
                      : setForm({ ...form, test_webhook_secret: e.target.value })
                  }
                  className="w-full text-xs font-mono p-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Currency */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700">Charge Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full text-xs p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="GBP">GBP - British Pound (£)</option>
                <option value="USD">USD - US Dollar ($)</option>
                <option value="EUR">EUR - Euro (€)</option>
                <option value="PKR">PKR - Pakistani Rupee (Rs)</option>
                <option value="SAR">SAR - Saudi Riyal (﷼)</option>
                <option value="AED">AED - UAE Dirham (د.إ)</option>
                <option value="CAD">CAD - Canadian Dollar ($)</option>
                <option value="AUD">AUD - Australian Dollar ($)</option>
              </select>
            </div>

            {/* Statement Descriptor */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700">Statement Descriptor</label>
              <input
                type="text"
                maxLength={22}
                placeholder="e.g. MY STORE POS"
                value={form.statement_descriptor}
                onChange={(e) => setForm({ ...form, statement_descriptor: e.target.value.slice(0, 22) })}
                className="w-full text-xs p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              />
              <p className="text-[10px] text-gray-400">Max 22 characters as displayed on customer bank statements.</p>
            </div>
          </div>

          {/* Webhook Endpoint Box */}
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-950 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-indigo-600" />
                Stripe Webhook Endpoint URL
              </span>
              <button
                type="button"
                onClick={handleCopyWebhook}
                className="text-xs font-bold text-indigo-700 hover:text-indigo-900 flex items-center gap-1 bg-white border border-indigo-200 px-2.5 py-1 rounded-md transition-colors"
              >
                {copiedWebhook ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedWebhook ? 'Copied' : 'Copy URL'}
              </button>
            </div>
            <div className="bg-white border border-indigo-200/80 rounded-lg p-2.5 font-mono text-[11px] text-indigo-900 break-all select-all">
              {settings?.webhook_url || 'Save settings to generate webhook URL'}
            </div>
            <p className="text-[11px] text-indigo-700/80 leading-relaxed">
              Add this endpoint in your{' '}
              <a
                href="https://dashboard.stripe.com/webhooks"
                target="_blank"
                rel="noreferrer"
                className="underline font-bold"
              >
                Stripe Webhook Dashboard
              </a>
              . Listen for <code className="bg-indigo-100 px-1 py-0.5 rounded text-[10px]">checkout.session.completed</code> and <code className="bg-indigo-100 px-1 py-0.5 rounded text-[10px]">payment_intent.succeeded</code>.
            </p>
          </div>

          {/* Test Connection Result Banner */}
          {testResult && (
            <div
              className={`p-4 rounded-xl border flex items-start gap-3 ${
                testResult.success
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-red-50 border-red-200 text-red-900'
              }`}
            >
              {testResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1 text-xs">
                <p className="font-bold">
                  {testResult.success ? 'Stripe Connection Successful!' : 'Connection Failed'}
                </p>
                {testResult.success ? (
                  <p className="text-emerald-700">
                    Account: <strong>{testResult.business_name || testResult.account_id}</strong> (
                    {testResult.mode} Mode). Supported currencies: {testResult.currencies?.join(', ') || form.currency}.
                  </p>
                ) : (
                  <p className="text-red-700 font-mono text-[11px]">{testResult.error}</p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              disabled={testingConnection || (!hasConfiguredSecret && !form.test_secret_key && !form.live_secret_key)}
              onClick={handleTestConnection}
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-300 text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            >
              {testingConnection ? <Loader2 className="w-4 h-4 animate-spin text-primary-600" /> : <Zap className="w-4 h-4 text-amber-500" />}
              Test Stripe Connection
            </button>

            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Stripe Configuration
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
